import { useState } from "react";
import { Link } from "react-router-dom";

import { ApiError } from "@/api/client";
import { useActivities, useCreateActivity, useUpdateActivity } from "@/api/crm";
import type { Activity } from "@/api/types";
import { Icon } from "@/components/icons";
import { Alert, EmptyState, PageHeader, Spinner, formatDate } from "@/components/ui";
import { fromLocalInput, toLocalInput } from "@/lib/format";
import { zoneLabel } from "@/lib/time";

type Tab = "open" | "overdue" | "done";

function TaskRow({ task }: { task: Activity }) {
  const update = useUpdateActivity();
  // Optimistic: the box ticks immediately and rolls back if the save fails.
  const [done, setDone] = useState(!!task.completed_at);
  const toggle = (next: boolean) => {
    setDone(next);
    update.mutate({ id: task.id, completed: next }, { onError: () => setDone(!next) });
  };
  const related = task.deal ? { to: `/deals/${task.deal.id}`, label: task.deal.name }
    : task.account ? { to: `/accounts/${task.account.id}`, label: task.account.name }
    : task.contact ? { to: `/contacts/${task.contact.id}`, label: task.contact.full_name } : null;
  return (
    <li className="flex items-start gap-3 px-4 py-3">
      <input type="checkbox" className="mt-1 h-4 w-4 accent-brand" checked={done}
        aria-label={`Mark “${task.subject}” done`} onChange={(e) => toggle(e.target.checked)} />
      <div className="min-w-0 flex-1">
        <p className={`text-sm font-medium ${done ? "text-muted line-through" : ""}`}>{task.subject}</p>
        <p className="text-xs text-muted">
          <span className={task.is_overdue ? "font-medium text-danger" : ""}>Due {formatDate(task.due_at!)}</span>
          {related && <> · <Link to={related.to} className="text-brand hover:underline">{related.label}</Link></>}
          {task.owner && <> · {task.owner.full_name}</>}
        </p>
      </div>
    </li>
  );
}

export function TasksPage() {
  const [tab, setTab] = useState<Tab>("open");
  const [mine, setMine] = useState(true);
  const tasks = useActivities({ kind: "task", status: tab, mine });
  const create = useCreateActivity();
  const [subject, setSubject] = useState("");
  const [due, setDue] = useState(() => toLocalInput(new Date(Date.now() + 86_400_000)));
  const [error, setError] = useState<string | null>(null);

  const add = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!subject.trim()) return;
    setError(null);
    try { await create.mutateAsync({ kind: "task", subject: subject.trim(), due_at: fromLocalInput(due) }); setSubject(""); }
    catch (err) { setError(err instanceof ApiError ? Object.values(err.fieldErrors())[0] ?? err.message : "Could not add task"); }
  };

  return (
    <>
      <PageHeader title="Tasks" subtitle="Follow-ups, quotes to send and visits to make." />
      <form onSubmit={(e) => void add(e)} className="card mb-4 flex flex-wrap items-end gap-2 p-4" aria-label="Add a task">
        <div className="min-w-48 flex-1"><label htmlFor="task-subject" className="label">New task</label>
          <input id="task-subject" className="input" placeholder="e.g. Send revised quote to Shakti Forgings" value={subject} onChange={(e) => setSubject(e.target.value)} /></div>
        <div><label htmlFor="task-due" className="label">Due <span className="font-normal text-muted">({zoneLabel()})</span></label><input id="task-due" type="datetime-local" className="input" value={due} onChange={(e) => setDue(e.target.value)} /></div>
        <button type="submit" className="btn-primary" disabled={create.isPending || !subject.trim()}><Icon name="plus" />Add</button>
      </form>
      {error && <div className="mb-3"><Alert>{error}</Alert></div>}
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="inline-flex rounded-md border border-line bg-surface p-0.5" role="tablist" aria-label="Task status">
          {(["open", "overdue", "done"] as const).map((t) => (
            <button key={t} type="button" role="tab" aria-selected={tab === t} onClick={() => setTab(t)}
              className={`rounded px-3 py-1 text-sm font-medium capitalize ${tab === t ? "bg-brand text-white" : "text-muted hover:text-ink"}`}>{t}</button>
          ))}
        </div>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" className="h-4 w-4 accent-brand" checked={mine} onChange={(e) => setMine(e.target.checked)} />Only my tasks</label>
      </div>
      {tasks.isLoading && <Spinner />}
      {tasks.data && tasks.data.items.length === 0 && (
        <EmptyState title={tab === "overdue" ? "Nothing overdue" : tab === "done" ? "No completed tasks" : "No open tasks"} body="Tasks you add here or on any record show up in this list." />
      )}
      {!!tasks.data?.items.length && <ul className="card divide-y divide-line" aria-label="Tasks">{tasks.data.items.map((t) => <TaskRow key={t.id} task={t} />)}</ul>}
    </>
  );
}
